/**
 * Path Normalization Utility
 * @date_of_change: 2025-10-07
 *
 * SINGLE SOURCE OF TRUTH for artwork path handling
 * Following AGENTS.md architectural principles
 *
 * CANONICAL FORMAT: "uploads/user_{clerkId}/token/{uuid}.webp"
 * - NO leading slash
 * - ALWAYS starts with "uploads/"
 * - User-based structure: uploads/user_ID/type/filename
 *   - type = "token" for characters/monsters
 *   - type = "map" for maps
 *
 * This matches the S3StorageProvider backend implementation (S3StorageProvider.ts:73-82)
 * and eliminates all path format ambiguity.
 */

/**
 * Extract canonical S3 object key from any artwork path format
 * @param {string} path - Can be full URL, /uploads/ path, or bare key
 * @returns {string|null} - Canonical key: "uploads/user_ID/type/file.ext" or null
 *
 * @example
 * extractCanonicalKey("https://s3.../uploads/user_2x9GSYL8c1CxoDU9fwkanFA7YEF/token_f72b5f34.webp")
 * // => "uploads/user_2x9GSYL8c1CxoDU9fwkanFA7YEF/token_f72b5f34.webp"
 *
 * extractCanonicalKey("/uploads/user_abc123/token/file.png")
 * // => "uploads/user_abc123/token/file.png"
 *
 * extractCanonicalKey("uploads/user_xyz/map/battlemap.webp")
 * // => "uploads/user_xyz/map/battlemap.webp"
 */
export function extractCanonicalKey(path) {
  if (!path || typeof path !== 'string') return null;
  
  const trimmed = path.trim();
  if (!trimmed) return null;

  // Case 0: Backend proxy URL with ?key=uploads/... query parameter
  if ((trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/api/')) && trimmed.includes('key=')) {
    try {
      const keyMatch = trimmed.match(/[?&]key=([^&#]+)/i);
      const decodedKey = keyMatch ? decodeURIComponent(keyMatch[1]) : '';
      if (decodedKey.startsWith('uploads/')) return decodedKey;
      if (decodedKey.startsWith('/uploads/')) return decodedKey.substring(1);
    } catch (_) {}
  }

  // Case 1: Full URL - extract everything after '/uploads/'
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    const uploadsIndex = trimmed.indexOf('/uploads/');
    if (uploadsIndex === -1) return null;
    return trimmed.substring(uploadsIndex + 1); // +1 to skip leading slash
  }
  
  // Case 2: Relative path starting with /uploads/
  if (trimmed.startsWith('/uploads/')) {
    return trimmed.substring(1); // Remove leading slash
  }
  
  // Case 3: Already canonical format
  if (trimmed.startsWith('uploads/')) {
    return trimmed;
  }
  
  // Invalid format
  return null;
}

/**
 * Convert canonical key to absolute URL for frontend consumption
 * @param {string} canonicalKey - Must be in canonical format
 * @returns {string} - Absolute URL: "https://backend.com/uploads/..."
 */
export function canonicalKeyToUrl(canonicalKey) {
  if (!canonicalKey) return null;
  
  // Ensure it's canonical format
  if (!canonicalKey.startsWith('uploads/')) {
    console.warn('[pathNormalizer] Invalid key format:', canonicalKey);
    return null;
  }
  
  // Add leading slash for URL path
  return `/${canonicalKey}`;
}

/**
 * Validate that a key is in canonical format
 * @param {string} key - Key to validate
 * @returns {boolean} - True if canonical
 *
 * @date_of_change: 2025-10-07 - Updated to match actual S3 structure
 * S3 path format: uploads/user_{clerkId}/token/{uuid}.webp
 */
export function isCanonicalKey(key) {
  if (!key || typeof key !== 'string') return false;

  // Must start with "uploads/" (no leading slash)
  // Must follow user-based structure: uploads/user_ID/type/filename
  // Type can be "token" or "map"
  // Also accept legacy format for backwards compatibility: uploads/characters/... or uploads/monsters/...
  const userBasedPattern = /^uploads\/user_[^/]+\/(token|map)\/[^/]+\.(png|jpg|jpeg|webp|svg)$/i;
  const legacyPattern = /^uploads\/(characters|monsters|maps)\/[^/]+\.(png|jpg|jpeg|webp|svg)$/i;

  return userBasedPattern.test(key) || legacyPattern.test(key);
}
